import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAction } from '@gadgetinc/react';
import { api } from '../api';
import { 
  Box, 
  VStack, 
  Text, 
  Button, 
  Heading,
  Container,
  useColorModeValue,
  Icon,
  HStack
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';

export default function SignOut() {
  const navigate = useNavigate();
  const [{ data, fetching, error }, runSignOut] = useAction(api.user.signOut);
  
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const brandColor = useColorModeValue('teal.600', 'teal.300');
  const secondaryColor = useColorModeValue('blue.600', 'blue.300');

  useEffect(() => {
    // Execute signout when component mounts
    runSignOut();
  }, [runSignOut]);

  useEffect(() => {
    // Auto-redirect after 1 second if signout is successful
    if (data) {
      const timer = setTimeout(() => {
        navigate('/sign-in');
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [data, navigate]);

  const handleManualRedirect = () => {
    navigate('/sign-in');
  };

  if (fetching) {
    return (
      <Box minH="100vh" bg={bgColor} display="flex" alignItems="center" justifyContent="center">
        <Container maxW="md">
          <VStack spacing={6}>
            <Box
              bg={cardBg}
              p={8}
              rounded="lg"
              shadow="lg"
              textAlign="center"
              w="full"
            >
              <Text fontSize="lg" color="gray.600">
                Signing you out...
              </Text>
            </Box>
          </VStack>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box minH="100vh" bg={bgColor} display="flex" alignItems="center" justifyContent="center">
        <Container maxW="md">
          <VStack spacing={6}>
            <Box
              bg={cardBg}
              p={8}
              rounded="lg"
              shadow="lg"
              textAlign="center"
              w="full"
            >
              <Text fontSize="lg" color="red.500" mb={4}>
                Error signing out: {error.toString()}
              </Text>
              <Button
                colorScheme="teal"
                onClick={handleManualRedirect}
                size="lg"
              >
                Continue to Sign In
              </Button>
            </Box>
          </VStack>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor} display="flex" alignItems="center" justifyContent="center">
      <Container maxW="md">
        <VStack spacing={6}>
          <Box
            bg={cardBg}
            p={8}
            rounded="lg"
            shadow="lg"
            textAlign="center"
            w="full"
            border="1px"
            borderColor="gray.200"
          >
            <VStack spacing={6}>
              <HStack spacing={2} justify="center">
                <Icon as={CheckCircleIcon} color="green.500" boxSize={8} />
              </HStack>
              
              <VStack spacing={3}>
                <Heading 
                  size="xl" 
                  color={brandColor}
                  fontWeight="bold"
                >
                  ElevationEMS AI
                </Heading>
                
                <Heading 
                  size="lg" 
                  color="gray.700"
                  fontWeight="semibold"
                >
                  Thank You!
                </Heading>

                <Text fontSize="lg" color="gray.600" textAlign="center">
                  You have been successfully signed out of your ElevationEMS AI account.
                </Text>

                <Text fontSize="md" color="gray.500" textAlign="center">
                  Your session has been cleared and all data has been secured.
                </Text>
              </VStack>

              <VStack spacing={3} w="full">
                <Button
                  colorScheme="teal"
                  size="lg"
                  onClick={handleManualRedirect}
                  w="full"
                  bg="gradient-linear(to-r, #319795, #2C7A7B)"
                  _hover={{
                    bg: "gradient-linear(to-r, #2C7A7B, #285E61)",
                    transform: "translateY(-2px)",
                    shadow: "lg"
                  }}
                  transition="all 0.2s"
                >
                  Return to Sign In
                </Button>

                <Text fontSize="sm" color="gray.400">
                  Redirecting automatically in 1 second...
                </Text>
              </VStack>
            </VStack>
          </Box>

          <Text fontSize="sm" color="gray.400" textAlign="center">
            Thank you for using ElevationEMS AI
          </Text>
        </VStack>
      </Container>
    </Box>
  );
}
